import { captureException } from '@sentry/nextjs'

import { UploadError } from './error'

/**
 * Components that can report upload errors to Sentry.
 */
export const UPLOAD_ERROR_COMPONENTS = {
  AddNoteFileSelect: 'AddNoteFileSelect',
  AddNoteFileUpload: 'AddNoteFileUpload',
  CreateDevices: 'CreateDevices',
  CreateFloorModal: 'CreateFloorModal',
  CreateQuickReportModal: 'CreateQuickReportModal',
  CreateSOPModal: 'CreateSOPModal',
  CreateUserFeedback: 'CreateUserFeedback',
  EditFloor: 'EditFloor',
  EditQuickReportModal: 'EditQuickReportModal',
  FileUpload: 'FileUpload',
  IncidentImageUpload: 'IncidentImageUpload',
  RadioGuards: 'RadioGuards',
} as const

export type UploadErrorComponent = keyof typeof UPLOAD_ERROR_COMPONENTS

/**
 * Reports file upload errors to Sentry with contextual information.
 *
 * - `UploadError` with context (validation errors): tagged with `errorType: 'validation'`
 * - `UploadError` without context (upload failures): tagged with `errorType: 'upload'`
 * - Other errors: no errorType tag
 *
 * @param error - The error to report
 * @param component - The component where the error occurred (defaults to 'FileUpload')
 */
export const reportUploadError = (
  error: unknown,
  component?: UploadErrorComponent
) => {
  if (error instanceof UploadError) {
    // Validation errors contain error context
    const isValidationError = error.context != null
    captureException(error, {
      tags: {
        component: component ?? UPLOAD_ERROR_COMPONENTS.FileUpload,
        action: 'FileUpload',
        errorType: isValidationError ? 'validation' : 'upload',
      },
      extra: error.context as Record<string, unknown>,
    })
  } else {
    captureException(error, {
      tags: {
        component: component ?? UPLOAD_ERROR_COMPONENTS.FileUpload,
      },
    })
  }
}
